import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useGetOptionsQuery, useLazyGenerateReportQuery } from './api/momSalaryReportApi';
import type {
  MomSalaryReportEmployeeOption,
  MomSalaryReportGridRequest,
  MomSalaryReportGridRow,
  MomSalaryReportPeriodOption,
} from './types';
import { extractErrorMessage } from '../../utils/errorUtils';
import { formatMoney } from '../reports/formatters';
import { formatVariance, getDefaultBasePeriod, getVarianceColor } from './utils';

export const MOM_SALARY_REPORT_DATA_SOURCE = 'MomSalaryReportGridItem';

const reportRequest = (
  reportingPeriodId: number,
  basePeriodId: number,
  employeeIds: number[]
): MomSalaryReportGridRequest => ({
  ReportingPeriodId: reportingPeriodId,
  BasePeriodId: basePeriodId,
  EmployeeIds: employeeIds,
});

const ReportRow = ({ row }: { row: MomSalaryReportGridRow }) => {
  const isTotal = row.employeeName === 'Total';
  const varianceColor = getVarianceColor(row.varianceUsd);

  return (
    <TableRow
      sx={isTotal ? { backgroundColor: 'action.hover', '& > *': { fontWeight: 700 } } : undefined}
    >
      <TableCell>{row.employeeName}</TableCell>
      <TableCell align="right">{formatMoney(row.basePeriodSalary)}</TableCell>
      <TableCell align="right">{formatMoney(row.reportingPeriodSalary)}</TableCell>
      <TableCell align="right" sx={{ color: varianceColor }}>
        {formatVariance(row.varianceUsd)}
      </TableCell>
      <TableCell align="right" sx={{ color: varianceColor }}>
        {row.variancePercentDisplay}
      </TableCell>
    </TableRow>
  );
};

export const MomSalaryReportPage = () => {
  const { data: options, isLoading: optionsLoading, error: optionsError } = useGetOptionsQuery();
  const [generateReport, { data: report, error: reportRequestError, isFetching: reportLoading }] =
    useLazyGenerateReportQuery();
  const [reportingPeriodId, setReportingPeriodId] = useState<number | null>(null);
  const [basePeriodId, setBasePeriodId] = useState<number | null>(null);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const initialized = useRef(false);

  const periods = options?.billingPeriods ?? [];
  const employees = useMemo(() => options?.employees ?? [], [options?.employees]);
  const defaultReportingPeriodId = periods[0]?.id ?? null;
  const defaultBasePeriodId = getDefaultBasePeriod(periods, defaultReportingPeriodId);
  const activeReportingPeriodId = reportingPeriodId ?? defaultReportingPeriodId;
  const activeBasePeriodId = basePeriodId ?? defaultBasePeriodId;

  const selectedEmployees = useMemo(
    () => employees.filter((employee) => selectedEmployeeIds.includes(employee.id)),
    [employees, selectedEmployeeIds]
  );

  useEffect(() => {
    if (initialized.current || !defaultReportingPeriodId || !defaultBasePeriodId) return;

    initialized.current = true;
    void generateReport(reportRequest(defaultReportingPeriodId, defaultBasePeriodId, []));
  }, [defaultBasePeriodId, defaultReportingPeriodId, generateReport]);

  const handleGenerate = () => {
    if (!activeReportingPeriodId || !activeBasePeriodId) {
      setError('Reporting Period and Base Period are required.');
      return;
    }

    setError(null);
    void generateReport(
      reportRequest(activeReportingPeriodId, activeBasePeriodId, selectedEmployeeIds)
    );
  };

  const handleClear = () => {
    setReportingPeriodId(null);
    setBasePeriodId(null);
    setSelectedEmployeeIds([]);
    setError(null);

    if (defaultReportingPeriodId && defaultBasePeriodId) {
      void generateReport(reportRequest(defaultReportingPeriodId, defaultBasePeriodId, []));
    }
  };

  const selectedReportingPeriod = periods.find((period) => period.id === activeReportingPeriodId);
  const selectedBasePeriod = periods.find((period) => period.id === activeBasePeriodId);
  const displayedRows = report?.rows ?? [];
  const isLoading = optionsLoading || reportLoading;

  return (
    <Box
      component="section"
      aria-label="MoM Salary Report"
      sx={{ p: { xs: 1, md: 2 }, height: '100%', overflow: 'auto' }}
    >
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
          <Autocomplete<MomSalaryReportPeriodOption>
            fullWidth
            options={periods}
            value={selectedReportingPeriod ?? null}
            onChange={(_event, value) => setReportingPeriodId(value?.id ?? null)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={isLoading}
            renderInput={(params) => <TextField {...params} label="Reporting Period" />}
          />

          <Autocomplete<MomSalaryReportPeriodOption>
            fullWidth
            options={periods}
            value={selectedBasePeriod ?? null}
            onChange={(_event, value) => setBasePeriodId(value?.id ?? null)}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={isLoading}
            renderInput={(params) => <TextField {...params} label="Base Period" />}
          />

          <Autocomplete<MomSalaryReportEmployeeOption, true>
            multiple
            fullWidth
            options={employees}
            value={selectedEmployees}
            onChange={(_event, value) =>
              setSelectedEmployeeIds(value.map((employee) => employee.id))
            }
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableCloseOnSelect
            disabled={isLoading}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox checked={selected} sx={{ mr: 1 }} />
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Employee" placeholder="All employees" />
            )}
          />

          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, height: 42 }}>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={isLoading}
              sx={{ height: '100%', whiteSpace: 'nowrap' }}
            >
              {reportLoading ? 'Loading...' : 'Generate Report'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClear}
              disabled={isLoading}
              sx={{ height: '100%' }}
            >
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {(optionsError || reportRequestError || error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error ||
            extractErrorMessage(
              reportRequestError || optionsError,
              'Failed to load MoM salary report.'
            )}
        </Alert>
      )}

      {isLoading && !report && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {report && (
        <Paper variant="outlined">
          <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedBasePeriod?.label || report.basePeriodCaption} /{' '}
              {selectedReportingPeriod?.label || report.reportingPeriodCaption}
            </Typography>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell align="right">{report.basePeriodCaption} (Base Period)</TableCell>
                  <TableCell align="right">
                    {report.reportingPeriodCaption} (Reporting Period)
                  </TableCell>
                  <TableCell align="right">Variance, USD</TableCell>
                  <TableCell align="right">Variance, %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedRows.length > 0 ? (
                  displayedRows.map((row) => <ReportRow key={row.id} row={row} />)
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No data
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};
